import type { DeletePatientDataListOut } from "@/server/schema/delete-patient-data";

/**
 * What the patient is about to confirm. Drives the copy and the button colour.
 * `scope` is the tier being acted on: one health system, one workspace, or
 * everything the portal sign-in reaches.
 */
export type ConfirmAction = {
  kind: "revoke" | "delete";
  scope: "one" | "organization" | "selection" | "all";
  facilityName?: string;
  organizationName?: string;
  selectedCount?: number;
};

/** One connected health system, as `deletePatientData.listFacilities` returns it. */
export type Facility = DeletePatientDataListOut["organizations"][number]["facilities"][number];

/** One workspace group of facilities, as `deletePatientData.listFacilities` returns it. */
export type FacilityOrganization = DeletePatientDataListOut["organizations"][number];

/** The authorized fields used to derive truthful confirmation scope and copy. */
export type ConfirmSelectionOrganization = {
  organizationId: string;
  organizationName: string;
  facilities: Array<Pick<Facility, "accountId" | "sourceName" | "deletionScheduledAt">>;
};

/** Props for the controlled delete-data confirmation dialog. */
export type ConfirmActionDialogProps = {
  action: ConfirmAction | null;
  pending: boolean;
  graceDays: number;
  onConfirm: () => void;
  onCancel: () => void;
};

/** Human-readable reasons a portal sign-in can bounce back here. */
export const SIGN_IN_ERRORS: Record<string, string> = {
  MISSING_SOURCE: "Choose a health system to sign in with.",
  SOURCE_NOT_FOUND: "We don't recognise that health system.",
  PROVIDER_NOT_CONFIGURED: "That health system isn't available here yet.",
  STATE_NOT_FOUND: "That sign-in took too long. Please try again.",
  STATE_EXPIRED: "That sign-in took too long. Please try again.",
  INVALID_STATE: "That sign-in couldn't be verified. Please try again.",
  ACCESS_DENIED: "You cancelled the sign-in at your health system.",
  PATIENT_INFO_NOT_FOUND: "Your health system didn't tell us who you are. Please try again.",
  RATE_LIMITED: "Too many attempts. Wait a moment and try again.",
};
